import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Grid3x3,
  ShieldCheck,
  BarChart3,
  CalendarDays,
  Settings,
  Building2,
  Boxes,
  ScrollText,
  Sparkles,
  KeyRound,
  ListChecks,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

// Top-level sidebar items.
const TOP_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/apps", label: "Apps", icon: Grid3x3 },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, perm: "calendar.read" },
  { to: "/reports", label: "Reports", icon: BarChart3, perm: "reports.read" },
];

// Tenant section: tenant-scoped admin surfaces. Tenant_admin sees all;
// non-admin roles see only what their perms allow. Parent hides when
// every child is filtered out.
const TENANT_CHILDREN = [
  { to: "/admin/users", label: "Users", icon: ShieldCheck, perm: "users.read" },
  { to: "/admin/roles", label: "Roles", icon: KeyRound, perm: "roles.read" },
  { to: "/admin/matrix", label: "Permissions", icon: ListChecks, perm: "roles.read" },
  { to: "/admin/apps", label: "App Registry", icon: Grid3x3, perm: "app_registry.read" },
  { to: "/admin/settings", label: "Settings", icon: Settings, perm: "tenant.manage" },
  { to: "/admin/vehicles", label: "Vehicles", icon: Boxes, perm: "calendar.admin" },
  { to: "/admin/calendar-types", label: "Calendar Types", icon: CalendarDays, perm: "calendar.admin" },
  { to: "/admin/clients", label: "Clients", icon: Building2, perm: "calendar.admin" },
];

const PLATFORM_ITEMS = [
  { to: "/platform", label: "Platform", icon: Sparkles, end: true },
  { to: "/platform/tenants", label: "Tenants", icon: Building2 },
  { to: "/platform/apps", label: "App Matrix", icon: Boxes },
  { to: "/platform/audit", label: "Audit", icon: ScrollText },
];

function navClass(isActive, inPlatform) {
  return cn(
    "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? inPlatform
        ? "bg-slate-900 text-amber-300"
        : "bg-cargodash-primary-50 text-cargodash-primary-700"
      : inPlatform
        ? "text-slate-300 hover:bg-slate-800 hover:text-amber-200"
        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
  );
}

function iconClass(isActive, inPlatform) {
  return cn(
    "h-4 w-4 shrink-0",
    isActive
      ? inPlatform
        ? "text-amber-300"
        : "text-cargodash-primary-600"
      : inPlatform
        ? "text-slate-400 group-hover:text-amber-200"
        : "text-slate-400 group-hover:text-slate-600"
  );
}

export default function Sidebar({ isOpen, onClose }) {
  const { user, hasPerm, activeTenant } = useAuth();
  const { pathname } = useLocation();
  const onPlatform = pathname.startsWith("/platform");
  const items = TOP_ITEMS.filter((i) => !i.perm || hasPerm(i.perm));
  const tenantChildren = TENANT_CHILDREN.filter((c) => !c.perm || hasPerm(c.perm));
  const showTenant = tenantChildren.length > 0;  // Phase 8: hide empty parent.
  const showPlatform = Boolean(user?.is_platform_admin);

  const displayName = onPlatform ? "BlackScott Holdings" : (activeTenant?.name || "BlackScott Hub");
  const displayInitials = (onPlatform ? "BlackScott Holdings" : (activeTenant?.name || "BlackScott"))
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // Tenant logo for the sidebar header. Driven entirely by
  // `branding.logo_url` from the API — null/empty falls back to the
  // initials swatch. Logos are static black wordmarks, so on the dark
  // platform-mode background we invert them to stay legible.
  const tenantLogo = onPlatform ? null : (activeTenant?.branding?.logo_url || null);

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm transition-opacity lg:hidden",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      />
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r transition-transform lg:static lg:translate-x-0",
          onPlatform
            ? "border-slate-800 bg-slate-950 text-slate-200"
            : "border-slate-200 bg-white text-slate-900",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div
          className={cn(
            "flex h-16 items-center justify-between border-b px-5",
            onPlatform ? "border-slate-800" : "border-slate-200"
          )}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            {tenantLogo ? (
              <img
                src={tenantLogo}
                alt={`${displayName} logo`}
                data-testid="sidebar-tenant-logo"
                className="h-8 w-auto max-w-[8.5rem] object-contain object-left"
                onError={(e) => {
                  // If the logo path 404s, fall back to the initials swatch.
                  e.currentTarget.dataset.failed = "1";
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-bold shadow-sm",
                  onPlatform ? "text-slate-950" : "text-white"
                )}
                style={{ background: onPlatform ? "#D9B14A" : "var(--brand-primary, #0f172a)" }}
                data-testid="sidebar-tenant-initials"
              >
                {displayInitials}
              </div>
            )}
            <div className="min-w-0 leading-tight">
              {!tenantLogo && (
                <div
                  className={cn(
                    "truncate font-display text-sm font-semibold tracking-tight",
                    onPlatform ? "text-amber-200" : "text-slate-900"
                  )}
                  data-testid="sidebar-tenant-name"
                >
                  {displayName}
                </div>
              )}
              <div
                className={cn(
                  "text-[10px] uppercase tracking-[0.16em]",
                  onPlatform ? "text-slate-500" : "text-slate-500"
                )}
              >
                {onPlatform ? "Platform" : "Hub"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              "rounded-md p-1.5 lg:hidden",
              onPlatform ? "text-slate-400 hover:bg-slate-800" : "text-slate-500 hover:bg-slate-100"
            )}
            aria-label="Close sidebar"
            data-testid="sidebar-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={onClose}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) => navClass(isActive, onPlatform)}
                  >
                    {({ isActive }) => (
                      <>
                        <Icon className={iconClass(isActive, onPlatform)} />
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          {showTenant && (
            <div className="mt-6" data-testid="sidebar-section-tenant">
              <div
                className={cn(
                  "px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em]",
                  onPlatform ? "text-slate-500" : "text-slate-400"
                )}
              >
                Tenant
              </div>
              <ul className="space-y-1">
                {tenantChildren.map((c) => {
                  const Icon = c.icon;
                  return (
                    <li key={c.to}>
                      <NavLink
                        to={c.to}
                        onClick={onClose}
                        data-testid={`nav-tenant-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) => navClass(isActive, onPlatform)}
                      >
                        {({ isActive }) => (
                          <>
                            <Icon className={iconClass(isActive, onPlatform)} />
                            <span>{c.label}</span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {showPlatform && (
            <div className="mt-6" data-testid="sidebar-section-platform">
              <div
                className={cn(
                  "px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em]",
                  onPlatform ? "text-amber-300/80" : "text-slate-400"
                )}
              >
                Platform
              </div>
              <ul className="space-y-1">
                {PLATFORM_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        onClick={onClose}
                        data-testid={`nav-platform-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) => navClass(isActive, onPlatform)}
                      >
                        {({ isActive }) => (
                          <>
                            <Icon className={iconClass(isActive, onPlatform)} />
                            <span>{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </nav>

        <div
          className={cn(
            "border-t px-5 py-3 text-[11px]",
            onPlatform ? "border-slate-800 text-slate-500" : "border-slate-200 text-slate-400"
          )}
        >
          v0.6.0 · Phase 6B
        </div>
      </aside>
    </>
  );
}
